import React from 'react';
import HighlightedText from 'components/HighlightedText';
import TagsRow from 'screens/Applications&WorkOrders/Components/RowItem/Components/TagsRow';
import FieldsDisplay from 'screens/Applications&WorkOrders/Components/RowItem/Components/FieldsDisplay';
import CropIconMapping from 'components/CropIconMapping';
import {
  SUBTITLE_COLOR,
  SELECTION_COLOR
} from 'screens/Applications&WorkOrders/Components/Constants';
import { PlanListSummary } from '../types';

export interface PlanRowItemProps {
  plan: PlanListSummary;
  isSelected?: boolean;
  searchTerm?: string;
}

const SEARCH_HIGHLIGHT_COLOR = '#C6ECFF';
const SEARCH_HIGHLIGHT_SELECTED_COLOR = '#97C7ED';
const NOT_AVAILABLE = 'N/A';
const NO_CROP = 'No Crop';

const Field: React.FC<{
  label: string;
  children: React.ReactNode;
  allowTruncate?: boolean;
}> = ({ label, children, allowTruncate }) => (
  <div className={allowTruncate ? 'shrink min-w-0' : 'shrink-0'}>
    <div className="text-xs font-normal" style={{ color: SUBTITLE_COLOR }}>
      {label}
    </div>
    <div className={`text-sm text-gray-900${allowTruncate ? ' min-w-0' : ''}`}>
      {children}
    </div>
  </div>
);

/**
 * List-row content for one Plan: name, Planned Area/Crop fields, tag
 * chips, and (when present) field info — mirroring InvoiceRowItem's
 * TagsRow/FieldsDisplay reuse. Crop truncates with an ellipsis and reads
 * "No Crop" when unset (FR-050). No Plan summary carries fieldsBrief yet,
 * so that section renders nothing until the API adds it.
 */
const PlanRowItem: React.FC<PlanRowItemProps> = ({
  plan,
  isSelected,
  searchTerm = ''
}) => {
  const flags = plan.flags ?? [];

  return (
    <div className="plan-item-content w-full flex flex-col justify-center items-start relative">
      {isSelected && (
        <div
          style={{
            width: '4px',
            position: 'absolute',
            top: '-4px',
            bottom: '-4px',
            left: '-16px',
            borderRadius: '0 4px 4px 0',
            background: SELECTION_COLOR
          }}
        />
      )}

      <div className="plan-name text-left text-base font-normal text-gray-900 w-full flex items-center justify-between">
        <HighlightedText
          text={plan.name}
          searchTerm={searchTerm}
          isSelected={isSelected}
          highlightColor={SEARCH_HIGHLIGHT_COLOR}
          selectedHighlightColor={SEARCH_HIGHLIGHT_SELECTED_COLOR}
          className="truncate flex-1 block"
          style={{ lineHeight: '24px', marginBottom: '4px' }}
          highlightPadding="0"
        />
      </div>

      <div className="plan-details flex items-start w-full gap-4">
        <Field label="Planned Area">
          {plan.estimatedAreaValue != null
            ? `${plan.estimatedAreaValue.toFixed(2)} ac`
            : NOT_AVAILABLE}
        </Field>
        <Field label="Crop" allowTruncate>
          <span className="flex w-full items-center gap-1 min-w-0">
            {plan.crop?.name && (
              <span className="shrink-0">
                <CropIconMapping
                  cropObject={{ name: plan.crop.name }}
                  size={16}
                />
              </span>
            )}
            <span className="min-w-0 flex-1 truncate">
              {plan.crop?.name || NO_CROP}
            </span>
          </span>
        </Field>
        {plan.fieldsBrief && (
          <div className="text-left shrink min-w-0">
            <FieldsDisplay
              fields={plan.fieldsBrief.firstFields || []}
              firstFieldInfo={plan.fieldsBrief.firstFieldInfo || {}}
              totalFields={plan.fieldsBrief.totalFields || 0}
              showSubheader
              iconWidth={14}
              iconHeight={14}
              iconColor="#868CA2"
              iconBorderColor="#868CA2"
              disablePositionOffset
              isSelected={isSelected}
            />
          </div>
        )}
      </div>

      {flags.length > 0 && (
        <div style={{ marginTop: '8px', width: '100%' }}>
          <TagsRow tagIds={flags.map(flag => flag.id)} allTags={flags} />
        </div>
      )}
    </div>
  );
};

export default PlanRowItem;
